/**
 * Personal Browser-AI-Tools Contracts
 *
 * Account-level Browser AI Tools DataTable: a user's own browser AI tools,
 * org-agnostic. Mirrors the org contract (./shadow-ai-browser-tools.ts) exactly,
 * with one column deviation — the `users`/`userCount` column is dropped (always
 * 1 in single-user scope). Everything is derived from the org contract; nothing
 * is re-authored.
 */
import { z } from 'zod';
import { baseListQuerySchema, sortOrderSchema, } from './helpers';
import { createTableQuerySchema, FilterOperators, FiltersSchema, FilterValueSchema, TableResponseMetaSchema, tableResponsePaginationSchema, } from './table';
import { browserAiToolListItemSchema, browserAiToolsSortFieldSchema, browserAiToolsFilterOptionFieldSchema, browserAiToolsTrendCategorySchema, BROWSER_AI_TOOLS_FILTERABLE_FIELDS, BROWSER_AI_TOOLS_SEARCHABLE_FIELDS, BROWSER_AI_TOOLS_FILTER_COLUMN_DATA_TYPES, BROWSER_AI_TOOLS_OPERATOR_OVERRIDES, BROWSER_AI_TOOLS_TREND_OPTIONS, } from './shadow-ai-browser-tools';
/** Scope-free org symbols, re-exported so personal consumers import from one place. */
export { BROWSER_AI_TOOLS_TREND_OPTIONS, browserAiToolsTrendCategorySchema, };
/**
 * Per-column operator overrides. Inherits org's date overrides (firstDetected/
 * lastSeen drop equals/notEquals, keep value-less) and additionally narrows
 * domain + name to includes/excludes/contains/not-contains plus the value-less
 * operators — the legacy screen offers no starts-with/ends-with.
 */
export const PERSONAL_BROWSER_AI_TOOLS_OPERATOR_OVERRIDES = {
    ...BROWSER_AI_TOOLS_OPERATOR_OVERRIDES,
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.DOMAIN]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.CONTAINS,
        FilterOperators.NOT_CONTAINS,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [BROWSER_AI_TOOLS_FILTERABLE_FIELDS.NAME]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.CONTAINS,
        FilterOperators.NOT_CONTAINS,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
};
/** Searchable columns, derived from org by dropping the users column. */
const { USERS: _searchUsers, ...PERSONAL_BROWSER_AI_TOOLS_SEARCHABLE_FIELDS } = BROWSER_AI_TOOLS_SEARCHABLE_FIELDS;
export { PERSONAL_BROWSER_AI_TOOLS_SEARCHABLE_FIELDS };
/** Sortable fields, narrowed from org by excluding users + logged-in user. */
export const personalBrowserAiToolsSortFieldSchema = browserAiToolsSortFieldSchema.exclude(['users', 'loggedInUser']);
/** Dynamic filter-option fields, narrowed from org by excluding users + logged-in user. */
export const personalBrowserAiToolsFilterOptionFieldSchema = browserAiToolsFilterOptionFieldSchema.exclude(['users', 'loggedInUser']);
/** Org row minus the users column the legacy browser screen never rendered. */
export const personalBrowserAiToolListItemSchema = browserAiToolListItemSchema.omit({ userCount: true });
/** Column → data-type map, derived from org by omitting the users key. */
const { users: _users, ...PERSONAL_BROWSER_AI_TOOLS_FILTER_COLUMN_DATA_TYPES } = BROWSER_AI_TOOLS_FILTER_COLUMN_DATA_TYPES;
/** Query for the personal filter-options endpoint (distinct values for one field). */
export const personalBrowserAiToolsFilterOptionsQuerySchema = z.object({
    field: personalBrowserAiToolsFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/** Personal filter options: plain distinct string values, with no user metadata. */
export const personalBrowserAiToolsFilterOptionsResponseSchema = z.object({
    field: personalBrowserAiToolsFilterOptionFieldSchema,
    options: z.array(z.string()),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
const personalBrowserAiToolsTableQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: personalBrowserAiToolsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
});
export const personalBrowserAiToolsTableQuerySchema = createTableQuerySchema(personalBrowserAiToolsTableQueryBaseSchema, PERSONAL_BROWSER_AI_TOOLS_FILTER_COLUMN_DATA_TYPES, PERSONAL_BROWSER_AI_TOOLS_OPERATOR_OVERRIDES).superRefine((data, ctx) => {
    data.filters.forEach((filter, index) => {
        if (filter.field === BROWSER_AI_TOOLS_FILTERABLE_FIELDS.FIRST_DETECTED ||
            filter.field === BROWSER_AI_TOOLS_FILTERABLE_FIELDS.LAST_SEEN) {
            filter.value.forEach((v, valueIndex) => {
                if (!z.string().datetime({ offset: true, local: true }).safeParse(v)
                    .success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Filter values for ${filter.field} field must be valid ISO 8601 datetime strings`,
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
        if (filter.field === BROWSER_AI_TOOLS_FILTERABLE_FIELDS.TREND) {
            filter.value.forEach((v, valueIndex) => {
                if (!browserAiToolsTrendCategorySchema.safeParse(v).success) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Filter values for trend field must be one of: ${browserAiToolsTrendCategorySchema.options.join(', ')}`,
                        path: ['filters', index, 'value', valueIndex],
                    });
                }
            });
        }
    });
});
/** Query params for the personal browser-tools list endpoint, serialized from DataTable state. */
export const listPersonalBrowserAiToolsDataTableParamsSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    sortBy: personalBrowserAiToolsSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: z.array(FilterValueSchema).optional(),
});
/** Personal browser-tools table response: paginated rows (org columns minus users). No lastHeartbeat. */
export const personalBrowserAiToolsTableResponseSchema = z.object({
    data: z.array(personalBrowserAiToolListItemSchema),
    pagination: tableResponsePaginationSchema,
    meta: TableResponseMetaSchema,
});
//# sourceMappingURL=account-shadow-ai-browser-tools.js.map